import { getStatusColors } from '@/components/cashflow/budget/getStatusColors';
import { DEFAULT_MARGIN } from '@/components/common/charts/chartHelper';
import ChartText from '@/components/common/charts/ChartText';
import NumericYAxisWithGrid from '@/components/common/charts/NumericYAxisWithGrid';
import { FlexRow } from '@/components/common/Flex';
import { toDollars } from '@/util/currency';
import { Box, Text, useToken } from '@chakra-ui/react';
import { BudgetPurpose } from '@common/graphql/generated';
import { getBudgetPurposeActionLabel } from '@common/react/budget';
import styleguide from '@common/styleguide';
import { capitalize } from '@common/util/strings';
import { toPx } from '@common/util/styles';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear } from '@visx/scale';

interface BudgetBarChartProps {
    width: number;
    budgetPurpose: BudgetPurpose;
    budgetedAmount: number;
    actualAmount: number;
}

const CHART_HEIGHT = 200;
const LABEL_HEIGHT = 30;
const LABEL_PADDING = 24;
const BAR_WIDTH = 48;

export const BudgetBarChart = ({
    width,
    budgetPurpose,
    budgetedAmount,
    actualAmount,
}: BudgetBarChartProps) => {
    const colors = getStatusColors({
        budgetPurpose,
        amountLeft: budgetedAmount - actualAmount,
    });

    // Convert Chakra color tokens to hex values
    const [progressColor] = useToken('colors', [colors.progress]);

    const innerWidth = width - DEFAULT_MARGIN.left - DEFAULT_MARGIN.right;
    const innerHeight =
        CHART_HEIGHT - DEFAULT_MARGIN.top - DEFAULT_MARGIN.bottom;

    const data = [
        { label: 'Budgeted', amount: budgetedAmount },
        {
            label: capitalize(getBudgetPurposeActionLabel(budgetPurpose)),
            amount: actualAmount,
        },
    ];

    const xScale = scaleBand<string>({
        domain: data.map((d) => d.label),
        range: [0, innerWidth],
        padding: 0.4,
    });

    const maxValue = Math.max(Math.abs(budgetedAmount), Math.abs(actualAmount));

    // Pad top and bottom to ensure the amount labels don't cut off
    const paddedMaxValue = roundToNearest50(maxValue * 1.4);

    // Only include negative domain if at least one value is negative
    const hasNegativeValue = budgetedAmount < 0 || actualAmount < 0;
    const yScale = scaleLinear<number>({
        domain: [hasNegativeValue ? -paddedMaxValue : 0, paddedMaxValue],
        range: [innerHeight, 0],
    });

    return (
        <FlexRow w={width} h={CHART_HEIGHT}>
            <svg width={width} height={CHART_HEIGHT}>
                <Group left={DEFAULT_MARGIN.left} top={DEFAULT_MARGIN.top}>
                    <NumericYAxisWithGrid
                        scale={yScale}
                        width={innerWidth}
                        height={innerHeight}
                        formatter={toDollars}
                        fontSize={styleguide.typography.fontSizes.sm}
                    />

                    {data.map((d) => {
                        const isPositive = d.amount >= 0;
                        const barHeight = Math.abs(
                            yScale(0) - yScale(d.amount)
                        );
                        const barX = xScale(d.label) ?? 0;
                        const barY = isPositive ? yScale(d.amount) : yScale(0);

                        return (
                            <g key={d.label}>
                                {/* Amount label */}
                                <foreignObject
                                    x={barX - LABEL_PADDING}
                                    y={barY - LABEL_HEIGHT}
                                    width={BAR_WIDTH + LABEL_PADDING * 2}
                                    height={LABEL_HEIGHT}
                                >
                                    <FlexRow justify="center">
                                        <Box
                                            rounded="full"
                                            border="1px solid"
                                            borderColor="gray.300"
                                            px={2}
                                            pb="2px"
                                            shadow="sm"
                                            bg="white"
                                        >
                                            <Text fontSize="sm">
                                                {toDollars(d.amount)}
                                            </Text>
                                        </Box>
                                    </FlexRow>
                                </foreignObject>

                                {/* Bar */}
                                {d.amount !== 0 && (
                                    <path
                                        d={buildBarPath({
                                            x: barX,
                                            y: barY,
                                            width: BAR_WIDTH,
                                            height: barHeight,
                                            radius: 6,
                                            isPositive,
                                        })}
                                        fill={
                                            d.label === 'Budgeted'
                                                ? styleguide.colors.grayLightX
                                                : progressColor
                                        }
                                    />
                                )}

                                {/* x-axis label */}
                                <ChartText
                                    x={barX + BAR_WIDTH / 2}
                                    y={innerHeight + 20}
                                    textAnchor="middle"
                                    fontSize={toPx(
                                        styleguide.typography.fontSizes.sm
                                    )}
                                >
                                    {d.label}
                                </ChartText>
                            </g>
                        );
                    })}
                </Group>
            </svg>
        </FlexRow>
    );
};

// Ensures that x-axis values are rounded to the nearest $50
const roundToNearest50 = (value: number) => {
    return Math.round(value / 5000) * 5000;
};

const buildBarPath = ({
    x,
    y,
    width,
    height,
    radius,
    isPositive,
}: {
    x: number;
    y: number;
    width: number;
    height: number;
    radius: number;
    isPositive: boolean;
}) => {
    // Needed custom paths here to handle rounded corners on the top/bottom of the bar
    if (isPositive) {
        return `
            M ${x} ${y + height}                    
            L ${x} ${y + radius}                    
            Q ${x} ${y} ${x + radius} ${y}        
            L ${x + width - radius} ${y}           
            Q ${x + width} ${y} ${x + width} ${y + radius}  
            L ${x + width} ${y + height}           
            Z                                       
        `;
    }

    return `
        M ${x} ${y}                    
        L ${x} ${y + height - radius}                    
        Q ${x} ${y + height} ${x + radius} ${y + height}        
        L ${x + width - radius} ${y + height}           
        Q ${x + width} ${y + height} ${x + width} ${y + height - radius}  
        L ${x + width} ${y}           
        Z                                       
    `;
};
